import React, {Dispatch, SetStateAction} from 'react'
import {Button, Icon, Number, Select, Text} from '@therms/atalaya'
import {
  LabeledInput,
  RecordTagTitles,
  RPCDocs,
  SelectRecordTag,
  toOptions,
  useRecordsFieldOptionsConfig,
  useUrlState,
} from '@therms/react-modules'
import {
  PersonSearch,
  RecordSearchState,
} from '@src/modules/Records/shared/RecordsList'
import {MdExpandLess, MdExpandMore} from 'react-icons/md'
import {IoClose} from 'react-icons/io5'
import {TbArrowBigRightFilled} from 'react-icons/tb'

interface RecordPersonSearchFiltersProps {
  age: {from?: number; to?: number} | undefined
  filtersState: PersonSearch | undefined
  searchText: string
  setAge: Dispatch<SetStateAction<undefined | {from?: number; to?: number}>>
  setFiltersState: Dispatch<SetStateAction<RecordSearchState>>
  setSearchText: Dispatch<SetStateAction<string>>
}

function RecordPersonSearchFilters({
  age,
  filtersState,
  searchText,
  setAge,
  setFiltersState,
  setSearchText,
}: RecordPersonSearchFiltersProps) {
  const [showAdvanceFilters, setAdvanceFilters] = useUrlState(
    'recordpersonfilters',
    false,
  )
  const config = useRecordsFieldOptionsConfig()

  return (
    <div className="flex w-full flex-col">
      <div className="pb-xxs gap-x-xxs inline-flex">
        <Text
          placeholder="Search..."
          onChangeValue={setSearchText}
          value={searchText}
          search
        />

        <Button
          onClick={() => setAdvanceFilters(!showAdvanceFilters)}
          size="base"
          subtle
          variant="neutral"
        >
          <span>Filters</span>
          {showAdvanceFilters ? (
            <Icon Icon={MdExpandLess} />
          ) : (
            <Icon Icon={MdExpandMore} />
          )}
        </Button>

        {(age?.from ||
          age?.to ||
          filtersState?.values?.gender ||
          filtersState?.values?.race ||
          !!filtersState?.values?.tags?.length ||
          !!searchText) && (
          <Button
            onClick={() => {
              setAdvanceFilters(false)
              setFiltersState({
                person: {
                  values: {gender: undefined, race: undefined, tags: []},
                },
              })
              setAge({from: undefined, to: undefined})
              setSearchText('')
            }}
            subtle
          >
            <Icon color="critical" Icon={IoClose} /> Clear
          </Button>
        )}
      </div>

      {showAdvanceFilters && (
        <div className="gap-xxs xxl:grid-cols-9 grid grid-cols-2 md:grid-cols-5 lg:grid-cols-9">
          <div className="col-span-2 lg:col-span-3">
            <LabeledInput
              label="Record tags"
              nameElement={
                <RecordTagTitles
                  recordTagIds={filtersState?.values?.tags}
                  listAll={false}
                />
              }
              inputElement={
                <SelectRecordTag
                  typeFilter={[
                    RPCDocs.Records.GetRecordTagsList.v1.TypeFilter.Person,
                  ]}
                  isClearable
                  combineSelected
                  menuIsOpen
                  multi
                  optionLabelExtractor={(option) => option.title}
                  optionValueExtractor={(option) => option.id}
                  onChangeValue={(val) =>
                    setFiltersState({
                      person: {values: {tags: val}},
                    })
                  }
                  placeholder="Record Tags"
                  value={filtersState?.values?.tags}
                />
              }
              value={filtersState?.values?.tags}
            />
          </div>

          <div className="col-span-2 md:col-span-3 lg:col-span-3">
            <LabeledInput
              label="Race"
              nameElement={<div>{filtersState?.values?.race}</div>}
              inputElement={
                <Select
                  allowCreate
                  isClearable
                  menuIsOpen
                  placeholder="Race"
                  options={toOptions(config?.person.race)}
                  onChangeValue={(val) => {
                    setFiltersState({person: {values: {race: val}}})
                  }}
                  value={filtersState?.values?.race}
                />
              }
              value={filtersState?.values?.race}
            />
          </div>

          <div className="col-span-2 lg:col-span-3">
            <LabeledInput
              label="Gender"
              nameElement={<div>{filtersState?.values?.gender}</div>}
              inputElement={
                <Select
                  allowCreate
                  isClearable
                  menuIsOpen
                  placeholder="Gender"
                  options={toOptions(config?.person.gender)}
                  onChangeValue={(val) => {
                    setFiltersState({person: {values: {gender: val}}})
                  }}
                  value={filtersState?.values?.gender}
                />
              }
              value={filtersState?.values?.gender}
            />
          </div>

          <div className="col-span-2 md:col-span-3 lg:col-span-5 xl:col-span-4">
            <LabeledInput
              label="Age"
              nameElement={
                <div className="text-xs">
                  {!!filtersState?.values?.age?.from &&
                    !filtersState?.values?.age?.to && (
                      <div>From {filtersState?.values?.age?.from}</div>
                    )}

                  {!!filtersState?.values?.age?.to &&
                    !filtersState?.values?.age?.from && (
                      <div>Up To {filtersState?.values?.age?.to}</div>
                    )}

                  {!!filtersState?.values?.age?.from &&
                    !!filtersState?.values?.age?.to && (
                      <div>
                        From {filtersState?.values?.age?.from} To{' '}
                        {filtersState?.values?.age?.to}
                      </div>
                    )}
                </div>
              }
              onCancel={() => setAge({from: undefined, to: undefined})}
              inputElement={
                <div className="gap-x-xxs bg-input-background inline-flex w-full items-center justify-center">
                  <Number
                    onChangeInteger={(val) => {
                      setAge({from: val, to: age?.to})
                    }}
                    placeholder="00"
                    value={age?.from}
                  />

                  <div className="px-xxs my-auto">
                    <Icon color="subtle" Icon={TbArrowBigRightFilled} />
                  </div>

                  <Number
                    onChangeInteger={(val) => {
                      setAge({from: age?.from, to: val})
                    }}
                    placeholder="00"
                    value={age?.to}
                  />
                </div>
              }
              value={
                !!filtersState?.values?.age?.from ||
                !!filtersState?.values?.age?.to
                  ? [
                      `${filtersState?.values?.age?.from}`,
                      `${filtersState?.values?.age?.to}`,
                    ]
                  : []
              }
            />
          </div>
        </div>
      )}
    </div>
  )
}

export {RecordPersonSearchFilters}